import type { Rect } from "../../game/input";
import { mFont } from "../renderConstants";

// ── Extracted screen function ───────────────────────────────────────

export function drawSideLaneThemeScreen(
  ctx: CanvasRenderingContext2D,
  laneWidth: number,
  laneHeight: number,
  level: number,
  alpha: number,
  mirrored: boolean,
  gameplayClockSec: number,
  levelBpm: number
): void {
  if (alpha <= 0 || laneWidth <= 1 || laneHeight <= 1) {
    return;
  }
  const motif = (Math.max(1, Math.floor(level)) - 1) % 6;
  const tempoScale = Math.max(0.1, levelBpm) / 120;
  const nowSec = gameplayClockSec * tempoScale;
  const hueBase = (level * 37) % 360;
  const rect: Rect = { x: 0, y: 0, w: laneWidth, h: laneHeight };
  const laneCenter = rect.w / 2;
  const mirrorPhase = mirrored ? Math.PI : 0;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.w, rect.h);
  ctx.clip();

  const laneGrad = ctx.createLinearGradient(rect.x, 0, rect.x + rect.w, laneHeight);
  laneGrad.addColorStop(0, "rgba(8,16,30,0.72)");
  laneGrad.addColorStop(1, "rgba(18,28,50,0.52)");
  ctx.fillStyle = laneGrad;
  ctx.fillRect(rect.x, 0, rect.w, laneHeight);

  if (motif === 0) {
    for (let i = 0; i < 12; i += 1) {
      const y = ((nowSec * 42 + i * 58) % (laneHeight + 40)) - 20;
      const widthT = 0.35 + 0.6 * ((i % 3) / 2);
      const w = Math.max(3, rect.w * widthT);
      const x = laneCenter - w / 2;
      ctx.strokeStyle = `hsla(${(hueBase + i * 14).toFixed(1)},100%,64%,0.7)`;
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, 16);
    }
  } else if (motif === 1) {
    for (let i = 0; i < 16; i += 1) {
      const y = ((nowSec * 28 + i * 36) % (laneHeight + 24)) - 12;
      const wobble = 0.5 + 0.5 * Math.sin(nowSec * 2.2 + i + mirrorPhase);
      const bw = 2 + ((i + level) % 4);
      const bh = 4 + ((i * 5) % 9);
      const x = rect.x + 1 + wobble * Math.max(0, rect.w - bw - 2);
      ctx.fillStyle = `hsla(${(hueBase + i * 20).toFixed(1)},100%,62%,0.75)`;
      ctx.fillRect(x, y, bw, bh);
    }
  } else if (motif === 2) {
    ctx.strokeStyle = `hsla(${(hueBase + 40).toFixed(1)},100%,65%,0.8)`;
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath();
      for (let y = 0; y <= laneHeight; y += 8) {
        const wave = Math.sin(y * 0.035 + nowSec * (3.4 + i * 0.6) + mirrorPhase + i) * (rect.w * 0.32);
        const x = laneCenter + wave;
        if (y === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    }
  } else if (motif === 3) {
    for (let i = 0; i < 6; i += 1) {
      const yStart = ((i * 78 + nowSec * 60) % (laneHeight + 70)) - 35;
      ctx.beginPath();
      for (let seg = 0; seg < 10; seg += 1) {
        const y = yStart + seg * 12;
        const x = seg % 2 === 0 ? rect.x + 2 : rect.x + rect.w - 2;
        if (seg === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.strokeStyle = `hsla(${(hueBase + i * 18).toFixed(1)},100%,60%,0.68)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  } else if (motif === 4) {
    const words = ["BIT", "BEAST", "GROOVE", "COMBO", "SYNC"];
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (let i = 0; i < 10; i += 1) {
      const y = ((i * 66 - nowSec * 26) % (laneHeight + 70)) - 20;
      const wobble = Math.sin(nowSec * 2 + i + mirrorPhase) * Math.max(0.5, rect.w * 0.08);
      const text = words[(i + level) % words.length] ?? "BIT";
      ctx.fillStyle = `hsla(${(hueBase + i * 16).toFixed(1)},100%,68%,0.7)`;
      ctx.fillText(text, laneCenter + wobble, y);
    }
  } else {
    for (let i = 0; i < 11; i += 1) {
      const y = ((i * 52 + nowSec * 34) % (laneHeight + 40)) - 20;
      const orbit = Math.sin(nowSec * 2.8 + i + mirrorPhase) * (rect.w * 0.22);
      const cx = laneCenter + orbit;
      const radius = Math.max(1, (1 - ((nowSec * 0.35 + i * 0.09) % 1)) * rect.w * 0.7);
      ctx.strokeStyle = `hsla(${(hueBase + i * 24).toFixed(1)},100%,64%,0.7)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  ctx.restore();
}
